import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '../lib/api-client';
import type { ITag, ICreateTagRequest, IUpdateTagRequest } from '@map-bldr/shared';

export function useTags() {
  return useQuery<ITag[]>({
    queryKey: ['tags'],
    queryFn: () => apiClient.get<ITag[]>('/tags'),
  });
}

export function useCreateTag() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: ICreateTagRequest) => {
      return apiClient.post<ITag>('/tags', data);
    },
    onMutate: async (data: ICreateTagRequest) => {
      const optimisticTag: ITag = {
        ...data,
        created_at: new Date().toISOString(),
      };

      queryClient.setQueryData<ITag[]>(['tags'], (old = []) => [optimisticTag, ...old]);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tags'] });
    },
  });
}

export interface IUpdateTagParams {
  tagId: string;
  data: IUpdateTagRequest;
}

export function useUpdateTag() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ tagId, data }: IUpdateTagParams) =>
      apiClient.patch<ITag>(`/tags/${tagId}`, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tags'] });
    },
  });
}

export function useDeleteTag() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (tagId: string) =>
      apiClient.delete<void>(`/tags/${tagId}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tags'] });
      queryClient.invalidateQueries({ queryKey: ['locations'] });
    },
  });
}
